import { useMemo, useState } from 'react';

import { ApiClient, formatDateTime, useAsync, useMutation } from '@clientma/core';
import { Alert, Badge, Button, Field, Input, Select, Skeleton, Textarea, useToast } from '@clientma/ui';

import {
  providerApi,
  type AccountNoteRow,
  type ProviderInvoiceLineInput,
  type AiWalletView,
  type ProviderTenantDetail,
} from '../api';

type Panel = 'timeline' | 'subscription' | 'invoice' | 'message' | 'credits';

/**
 * The things an operator does to a customer's account, in one place.
 *
 * Kept together rather than spread across five screens because they are one job: somebody
 * finishes a call and has to change the plan, raise the invoice for the extra seats, tell them
 * it is done, and write down what was agreed. Splitting that across tabs would mean typing the
 * same context four times.
 *
 * Every action requires a reason, and every reason lands on the timeline below.
 */
export function AccountActions({
  api,
  tenantId,
  detail,
  onChanged,
}: {
  api: ApiClient;
  tenantId: string;
  detail: ProviderTenantDetail;
  onChanged: () => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);
  const toast = useToast();

  const [panel, setPanel] = useState<Panel>('timeline');

  const notes = useAsync((signal) => provider.accountNotes(tenantId, signal), [provider, tenantId]);

  const refresh = () => {
    notes.reload();
    onChanged();
  };

  return (
    <div className="cma-stack">
      <div className="provider-tabs provider-tabs--inline" role="tablist">
        {(
          [
            ['timeline', 'History'],
            ['subscription', 'Subscription'],
            ['invoice', 'Raise invoice'],
            ['message', 'Message'],
            ['credits', 'AI credits'],
          ] as [Panel, string][]
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={panel === key}
            className={`provider-tab${panel === key ? ' provider-tab--active' : ''}`}
            onClick={() => setPanel(key)}
          >
            {label}
          </button>
        ))}
      </div>

      {panel === 'timeline' && <Timeline notes={notes.data} isLoading={notes.isLoading} />}

      {panel === 'subscription' && (
        <SubscriptionPanel
          api={api}
          tenantId={tenantId}
          detail={detail}
          onDone={() => {
            toast.success('The subscription was changed');
            setPanel('timeline');
            refresh();
          }}
        />
      )}

      {panel === 'invoice' && (
        <InvoicePanel
          api={api}
          tenantId={tenantId}
          detail={detail}
          onDone={(issued) => {
            toast.success(issued ? 'Invoice raised and sent' : 'Draft invoice created');
            setPanel('timeline');
            refresh();
          }}
        />
      )}

      {panel === 'message' && (
        <MessagePanel
          api={api}
          tenantId={tenantId}
          onDone={(recipients) => {
            toast.success('Message sent', recipients.join(', '));
            setPanel('timeline');
            refresh();
          }}
        />
      )}

      {panel === 'credits' && (
        <CreditsPanel
          api={api}
          tenantId={tenantId}
          onDone={() => {
            toast.success('AI credits updated');
            setPanel('timeline');
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ── the history

function Timeline({ notes, isLoading }: { notes?: AccountNoteRow[] | null; isLoading: boolean }) {
  if (isLoading) return <Skeleton height={160} />;

  if (!notes || notes.length === 0) {
    return (
      <p className="provider-muted">
        Nothing has been recorded against this customer yet. Every change made here appears in
        this list with the reason it was made.
      </p>
    );
  }

  return (
    <div className="cma-stack cma-stack--tight">
      {notes.map((note) => (
        <div key={note.id} className="provider-entry">
          <div className="provider-entry__head">
            <strong>{note.subject}</strong>
            <Badge tone={toneFor(note.kind)}>{spaced(note.kind)}</Badge>
            {/*
              Said on the row rather than in a legend. An operator scanning this needs to know
              at a glance which lines the customer has also read.
            */}
            {note.isVisibleToCustomer && <Badge tone="info">Customer saw this</Badge>}
          </div>

          {note.body && <p className="provider-entry__body">{note.body}</p>}

          <p className="provider-muted">
            {note.authorName ?? 'ClientMa'} · {formatDateTime(note.occurredAtUtc, 'en-SG')}
          </p>
        </div>
      ))}
    </div>
  );
}

// ── changing what they are on

function SubscriptionPanel({
  api,
  tenantId,
  detail,
  onDone,
}: {
  api: ApiClient;
  tenantId: string;
  detail: ProviderTenantDetail;
  onDone: () => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);

  const plans = useAsync((signal) => provider.plans(signal), [provider]);

  const [planId, setPlanId] = useState('');
  const [seats, setSeats] = useState('');
  const [interval, setInterval] = useState('');
  const [status, setStatus] = useState('');
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState<'' | 'yes' | 'no'>('');
  const [billingEmail, setBillingEmail] = useState(detail.billingEmail ?? '');
  const [note, setNote] = useState('');

  const save = useMutation(
    () =>
      provider.changeSubscription(tenantId, {
        planId: planId || null,
        seats: seats ? Number(seats) : null,
        interval: interval || null,
        status: status || null,
        cancelAtPeriodEnd: cancelAtPeriodEnd === '' ? null : cancelAtPeriodEnd === 'yes',
        // Sent only when it differs, so a form that shows one field cannot clear another it
        // never showed.
        billingEmail: billingEmail === (detail.billingEmail ?? '') ? null : billingEmail,
        note,
      }),
    { onSuccess: onDone },
  );

  return (
    <div className="cma-stack">
      <Alert tone="info" title="This changes what the customer is billed">
        Entitlements are rebuilt from the plan, so a change here takes effect immediately —
        including the limits their people run into.
      </Alert>

      <Field label="Plan" hint="Leave as is to keep the current one.">
        {(id) => (
          <Select id={id} value={planId} onChange={(event) => setPlanId(event.target.value)}>
            <option value="">Unchanged</option>
            {(plans.data ?? [])
              .filter((plan) => plan.isActive)
              .map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} ({plan.tier})
                </option>
              ))}
          </Select>
        )}
      </Field>

      <Field label="Seats">
        {(id) => (
          <Input
            id={id}
            type="number"
            min={1}
            value={seats}
            placeholder="Unchanged"
            onChange={(event) => setSeats(event.target.value)}
          />
        )}
      </Field>

      <Field label="Billing interval">
        {(id) => (
          <Select id={id} value={interval} onChange={(event) => setInterval(event.target.value)}>
            <option value="">Unchanged</option>
            <option value="Monthly">Monthly</option>
            <option value="Quarterly">Quarterly</option>
            <option value="Annual">Annual</option>
          </Select>
        )}
      </Field>

      <Field label="Subscription status">
        {(id) => (
          <Select id={id} value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">Unchanged</option>
            <option value="Trialing">Trialing</option>
            <option value="Active">Active</option>
            <option value="PastDue">Past due</option>
            <option value="Cancelled">Cancelled</option>
          </Select>
        )}
      </Field>

      <Field label="Ending at the end of this term">
        {(id) => (
          <Select
            id={id}
            value={cancelAtPeriodEnd}
            onChange={(event) => setCancelAtPeriodEnd(event.target.value as '' | 'yes' | 'no')}
          >
            <option value="">Unchanged</option>
            <option value="yes">Yes, do not renew</option>
            <option value="no">No, keep renewing</option>
          </Select>
        )}
      </Field>

      <Field label="Billing email">
        {(id) => (
          <Input
            id={id}
            type="email"
            value={billingEmail}
            onChange={(event) => setBillingEmail(event.target.value)}
          />
        )}
      </Field>

      <Field
        label="Why"
        hint="Required. It goes on the customer's record and stays there."
        required
      >
        {(id) => (
          <Textarea
            id={id}
            rows={3}
            value={note}
            placeholder="Agreed on the call with Priya — adding 28 seats for the new warehouse."
            onChange={(event) => setNote(event.target.value)}
          />
        )}
      </Field>

      {save.error && <Alert tone="danger">{save.error.message}</Alert>}

      <Button
        disabled={note.trim().length < 5 || save.isRunning}
        onClick={() => void save.run(undefined as never)}
      >
        {save.isRunning ? 'Saving…' : 'Change the subscription'}
      </Button>
    </div>
  );
}

// ── raising an invoice

function InvoicePanel({
  api,
  tenantId,
  detail,
  onDone,
}: {
  api: ApiClient;
  tenantId: string;
  detail: ProviderTenantDetail;
  onDone: (issued: boolean) => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);

  const [lines, setLines] = useState<ProviderInvoiceLineInput[]>([
    { description: '', quantity: 1, unitAmount: 0 },
  ]);
  const [dueDate, setDueDate] = useState('');
  const [purchaseOrder, setPurchaseOrder] = useState('');
  const [reason, setReason] = useState('');
  const [issue, setIssue] = useState(true);
  const [sendEmail, setSendEmail] = useState(true);

  const subtotal = lines.reduce((sum, line) => sum + line.quantity * line.unitAmount, 0);

  const raise = useMutation(
    () =>
      provider.raiseInvoice(tenantId, {
        currency: detail.summary.currency,
        lines: lines.filter((line) => line.description.trim().length > 0),
        dueDate: dueDate || null,
        purchaseOrderNumber: purchaseOrder || null,
        taxRate: null,
        issue,
        sendEmail: issue && sendEmail,
        reason,
      }),
    { onSuccess: () => onDone(issue) },
  );

  const update = (index: number, patch: Partial<ProviderInvoiceLineInput>) =>
    setLines((current) =>
      current.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    );

  return (
    <div className="cma-stack">
      <Alert tone="info" title="For something the subscription does not bill">
        Training, migration, a one-off overage. Tax and payment terms come from the customer&rsquo;s
        own country profile.
      </Alert>

      {lines.map((line, index) => (
        <div key={index} className="provider-invoice-line">
          <Input
            value={line.description}
            placeholder="What it is for"
            aria-label={`Line ${index + 1} description`}
            onChange={(event) => update(index, { description: event.target.value })}
          />
          <Input
            type="number"
            min={0}
            step="0.01"
            value={line.quantity}
            aria-label={`Line ${index + 1} quantity`}
            onChange={(event) => update(index, { quantity: Number(event.target.value) })}
          />
          <Input
            type="number"
            min={0}
            step="0.01"
            value={line.unitAmount}
            aria-label={`Line ${index + 1} unit amount`}
            onChange={(event) => update(index, { unitAmount: Number(event.target.value) })}
          />
          {lines.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={`Remove line ${index + 1}`}
              onClick={() => setLines((current) => current.filter((_, p) => p !== index))}
            >
              ✕
            </Button>
          )}
        </div>
      ))}

      <Button
        variant="ghost"
        size="sm"
        onClick={() =>
          setLines((current) => [...current, { description: '', quantity: 1, unitAmount: 0 }])
        }
      >
        Add a line
      </Button>

      <div className="provider-linerow">
        <span className="provider-muted">Before tax</span>
        <span className="provider-num">
          {detail.summary.currency} {subtotal.toFixed(2)}
        </span>
      </div>

      <Field label="Due date" hint="Empty uses the payment terms for their country.">
        {(id) => (
          <Input
            id={id}
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
          />
        )}
      </Field>

      <Field label="Their purchase order">
        {(id) => (
          <Input
            id={id}
            value={purchaseOrder}
            onChange={(event) => setPurchaseOrder(event.target.value)}
          />
        )}
      </Field>

      <Field label="What this invoice is for" required>
        {(id) => (
          <Textarea
            id={id}
            rows={2}
            value={reason}
            placeholder="Two days of on-site migration support, agreed in the December call."
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>

      <label className="cma-check">
        <input type="checkbox" checked={issue} onChange={(e) => setIssue(e.target.checked)} />
        <span>Issue it now. Leave unticked to keep it as a draft.</span>
      </label>

      <label className="cma-check">
        <input
          type="checkbox"
          checked={sendEmail}
          disabled={!issue}
          onChange={(event) => setSendEmail(event.target.checked)}
        />
        <span>Email it to {detail.billingEmail ?? 'their billing contact'}</span>
      </label>

      {!detail.billingEmail && issue && sendEmail && (
        <Alert tone="warning" title="There is no billing email on file">
          The invoice will be raised, but nobody will be told about it. Add a billing contact
          under Subscription first.
        </Alert>
      )}

      {raise.error && <Alert tone="danger">{raise.error.message}</Alert>}

      <Button
        disabled={
          raise.isRunning ||
          reason.trim().length === 0 ||
          lines.every((line) => line.description.trim().length === 0)
        }
        onClick={() => void raise.run(undefined as never)}
      >
        {raise.isRunning ? 'Raising…' : issue ? 'Raise and issue' : 'Save as draft'}
      </Button>
    </div>
  );
}

// ── telling them something

function MessagePanel({
  api,
  tenantId,
  onDone,
}: {
  api: ApiClient;
  tenantId: string;
  onDone: (recipients: string[]) => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [audience, setAudience] = useState('billing');
  const [notifyInApp, setNotifyInApp] = useState(true);

  const [problem, setProblem] = useState<string | null>(null);

  const send = useMutation(
    () => provider.messageTenant(tenantId, { subject, body, audience, notifyInApp }),
    {
      onSuccess: (result) => {
        // Kept here rather than read off the mutation: it tracks whether a call is running,
        // not what the last one returned, and a partial delivery is exactly the case somebody
        // has to be told about.
        setProblem(result.problem ?? null);
        if (!result.problem) onDone(result.recipients);
      },
    },
  );

  return (
    <div className="cma-stack">
      <Field label="Who it goes to">
        {(id) => (
          <Select id={id} value={audience} onChange={(event) => setAudience(event.target.value)}>
            <option value="billing">Their billing contact</option>
            <option value="admins">Their administrators</option>
            <option value="both">Both</option>
          </Select>
        )}
      </Field>

      <Field label="Subject" required>
        {(id) => (
          <Input id={id} value={subject} onChange={(event) => setSubject(event.target.value)} />
        )}
      </Field>

      <Field label="Message" required>
        {(id) => (
          <Textarea
            id={id}
            rows={6}
            value={body}
            onChange={(event) => setBody(event.target.value)}
          />
        )}
      </Field>

      <label className="cma-check">
        <input
          type="checkbox"
          checked={notifyInApp}
          onChange={(event) => setNotifyInApp(event.target.checked)}
        />
        <span>Also show it in their notifications, not only by email</span>
      </label>

      {send.error && <Alert tone="danger">{send.error.message}</Alert>}

      {problem && (
        <Alert tone="warning" title="It did not reach everybody">
          {problem}
        </Alert>
      )}

      <Button
        disabled={send.isRunning || subject.trim().length === 0 || body.trim().length === 0}
        onClick={() => void send.run(undefined as never)}
      >
        {send.isRunning ? 'Sending…' : 'Send'}
      </Button>
    </div>
  );
}

// ── AI credits

function CreditsPanel({
  api,
  tenantId,
  onDone,
}: {
  api: ApiClient;
  tenantId: string;
  onDone: () => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);

  const [credits, setCredits] = useState('');
  const [allowance, setAllowance] = useState('');
  const [reason, setReason] = useState('');

  const [wallet, setWallet] = useState<AiWalletView | null>(null);

  const grant = useMutation(
    () =>
      provider.grantAiCredits(tenantId, {
        credits: credits ? Number(credits) : null,
        // Empty means "whatever their plan gives", which is the default rather than nothing.
        monthlyAllowance: allowance === '' ? null : Number(allowance),
        reason,
      }),
    {
      onSuccess: (result) => {
        setWallet(result);
        onDone();
      },
    },
  );

  return (
    <div className="cma-stack">
      <Alert tone="info" title="Credits are the AI allowance, not money">
        Each customer gets an allowance every month from their plan. A number here overrides
        that for this customer only; a one-off grant is added on top straight away.
      </Alert>

      <Field label="Grant now" hint="A one-off top-up. Leave empty to change only the monthly figure.">
        {(id) => (
          <Input
            id={id}
            type="number"
            min={1}
            value={credits}
            onChange={(event) => setCredits(event.target.value)}
          />
        )}
      </Field>

      <Field
        label="Monthly allowance for this customer"
        hint="Empty means whatever their plan includes."
      >
        {(id) => (
          <Input
            id={id}
            type="number"
            min={0}
            value={allowance}
            placeholder="Plan default"
            onChange={(event) => setAllowance(event.target.value)}
          />
        )}
      </Field>

      <Field label="Why">
        {(id) => (
          <Textarea
            id={id}
            rows={2}
            value={reason}
            placeholder="Goodwill after the outage on the 12th."
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>

      {wallet && (
        <Alert tone="success" title="Their wallet now">
          {wallet.creditsAvailable} available of {wallet.creditsGranted} granted
          {wallet.monthlyAllowance ? `, ${wallet.monthlyAllowance} a month` : ''}
        </Alert>
      )}

      {grant.error && <Alert tone="danger">{grant.error.message}</Alert>}

      <Button
        disabled={grant.isRunning || (credits === '' && allowance === '')}
        onClick={() => void grant.run(undefined as never)}
      >
        {grant.isRunning ? 'Saving…' : 'Apply'}
      </Button>
    </div>
  );
}

// ── shaping

function toneFor(kind: string) {
  switch (kind) {
    case 'SubscriptionChange':
      return 'warning' as const;
    case 'Invoice':
      return 'accent' as const;
    case 'MessageSent':
      return 'info' as const;
    case 'Payment':
      return 'success' as const;
    default:
      return 'neutral' as const;
  }
}

/** SubscriptionChange → Subscription change. The enum name is not a label. */
function spaced(kind: string) {
  const words = kind.replace(/([a-z])([A-Z])/g, '$1 $2');
  return words.charAt(0) + words.slice(1).toLowerCase();
}
